import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, ArrowLeft, Check, Globe, Smartphone, Laptop, Server } from 'lucide-react';
import { useServicePrincipals } from '@/store/service-principal';
import { Button } from '@/components/ui/Button';
import type { ServicePrincipal } from '@/types';

const platforms = [
    { id: 'web', name: 'Web Application', description: 'Websites, portals, and server-side backend apps.', icon: Globe },
    { id: 'mobile', name: 'Mobile Application', description: 'iOS, Android, or tablet native applications.', icon: Smartphone },
    { id: 'desktop', name: 'Desktop Application', description: 'macOS, Windows, or Linux client applications.', icon: Laptop },
    { id: 'service', name: 'Daemon / Service', description: 'Machine-to-machine integrations, cron jobs, and CLIs.', icon: Server },
];

export const CreateServicePrincipalPage = () => {
    const navigate = useNavigate();
    const createServicePrincipal = useServicePrincipals((state) => state.create);

    const [loading, setLoading] = useState(false);
    const [createdResponse, setCreatedResponse] = useState<ServicePrincipal | null>(null);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
    });
    const [selectedPlatform, setSelectedPlatform] = useState('web');

    const platformMap: Record<string, number> = {
        web: 1,
        mobile: 2,
        desktop: 3,
        service: 4,
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const result = await createServicePrincipal({
                name: formData.name,
                description: formData.description || undefined,
                platform: platformMap[selectedPlatform] || 1,
            });
            setCreatedResponse(result);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (createdResponse) {
        return (
            <div className="p-4 lg:p-8 max-w-2xl mx-auto w-full animate-in fade-in duration-500">
                <div className="bg-white dark:bg-slate-950 rounded-2xl p-8 border border-slate-100 dark:border-slate-900 text-center">
                    <div className="w-16 h-16 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Check className="w-8 h-8" />
                    </div>
                    <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2">Service Principal Created</h2>
                    <p className="text-slate-500 dark:text-slate-400 mb-8 max-w-md mx-auto">
                        Your service principal <strong>{createdResponse.name}</strong> has been successfully created. You can now generate secrets and credentials for this service principal.
                    </p>

                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-6 text-left border border-slate-100 dark:border-slate-800 mb-8">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">
                                    Client ID
                                </label>
                                <div className="font-mono text-sm bg-white dark:bg-slate-900 p-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300">
                                    {createdResponse.client_id}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <Button
                            variant="outline"
                            className="flex-1 rounded-xl h-11 border-slate-200 dark:border-slate-700"
                            onClick={() => navigate('/m/service-principals')}
                        >
                            Back to List
                        </Button>
                        <Button
                            className="flex-1 bg-[#1e293b] hover:bg-slate-800 text-white rounded-xl h-11"
                            onClick={() => navigate(`/m/service-principals/${createdResponse.id}`)}
                        >
                            Manage Secrets
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 lg:p-8 max-w-2xl mx-auto w-full animate-in fade-in duration-500">
            <button
                onClick={() => navigate('/m/service-principals')}
                className="flex items-center text-sm font-medium text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200 transition-colors mb-6 group"
            >
                <ArrowLeft className="w-4 h-4 mr-1 transition-transform group-hover:-translate-x-1" />
                Back to service principals
            </button>

            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                        <Bot className="w-5 h-5 text-(--primary)" />
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
                        Create Service Principal
                    </h1>
                </div>
                <p className="text-slate-500 dark:text-slate-400">
                    Register a new non-human identity for programmatic access.
                </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-950 rounded-2xl p-6 lg:p-8 border border-slate-100 dark:border-slate-900 space-y-6">
                <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full h-11 px-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-(--primary)/20 focus:border-(--primary) transition-all"
                        placeholder="e.g., CI/CD Pipeline"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Description
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full h-24 p-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-(--primary)/20 focus:border-(--primary) transition-all resize-none"
                        placeholder="Briefly describe what this service principal will be used for..."
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                        Platform <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {platforms.map((platform) => {
                            const Icon = platform.icon;
                            const isSelected = selectedPlatform === platform.id;
                            return (
                                <div
                                    key={platform.id}
                                    onClick={() => setSelectedPlatform(platform.id)}
                                    className={`cursor-pointer p-4 rounded-2xl border transition-all flex gap-3.5 items-start ${
                                        isSelected
                                            ? 'border-(--primary) bg-(--primary-light)/10 dark:bg-(--primary)/5 shadow-sm'
                                            : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-slate-50/50 dark:hover:bg-slate-800/30'
                                    }`}
                                >
                                    <div className={`p-2 rounded-xl shrink-0 transition-colors ${
                                        isSelected
                                            ? 'bg-(--primary) text-white'
                                            : 'bg-slate-100 dark:bg-slate-800 text-slate-500'
                                    }`}>
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <div className={`text-sm font-bold transition-colors ${
                                            isSelected ? 'text-(--primary)' : 'text-slate-800 dark:text-slate-200'
                                        }`}>
                                            {platform.name}
                                        </div>
                                        <div className="text-xs text-slate-400 dark:text-slate-500 mt-1 leading-normal">
                                            {platform.description}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate('/m/service-principals')}
                        className="rounded-xl px-6 h-11 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        isLoading={loading}
                        className="bg-[#1e293b] hover:bg-slate-800 text-white rounded-xl px-6 h-11"
                    >
                        Create Service Principal
                    </Button>
                </div>
            </form>
        </div>
    );
};
